import React, { useMemo, useState } from "react"

import { CRITERION_VALUE } from "@/constants/criterion-values"
import { AlertTriangle, Check, ChevronDown, X } from "lucide-react"

import { cn } from "@/utils/cn"

import type { CriterionResult } from "../../types"

export interface CompositeReviewer {
  key: string
  label: string
  /** Results aligned by index with the canonical criterion list. */
  results?: CriterionResult[]
  assignee?: string | null
  date?: string | null
  /** Text shown for a criterion when this reviewer has no result. */
  emptyMessage?: string
}

interface CompositeReviewMatrixProps {
  /**
   * Canonical criterion list. Reviewers with results shorter than this list
   * are treated as "no result" for the missing rows.
   */
  criteria: string[]
  reviewers: CompositeReviewer[]
  /** Start with every criterion expanded. Defaults to first-open only. */
  defaultAllExpanded?: boolean
  /** Singular label for one compared item. */
  itemLabel?: string
  /** Show reviewer names once as columns instead of repeating them per item. */
  reviewerColumns?: boolean
}

const VerdictIcon: React.FC<{
  result: CriterionResult["result"] | null | undefined
}> = ({ result }) => {
  const base = "w-3.5 h-3.5 flex-shrink-0"
  if (result === CRITERION_VALUE.MET) {
    return <Check className={cn(base, "text-coval-success-text")} />
  }
  if (result === CRITERION_VALUE.NOT_MET) {
    return <X className={cn(base, "text-coval-danger-text")} />
  }
  if (result === CRITERION_VALUE.UNKNOWN) {
    return <AlertTriangle className={cn(base, "text-coval-warning-text")} />
  }
  return (
    <span
      className="inline-block w-3.5 h-3.5 rounded-full border border-dashed border-coval-border"
      aria-hidden
    />
  )
}

const verdictLabel = (
  result: CriterionResult["result"] | null | undefined,
): string => {
  if (result === CRITERION_VALUE.MET) {
    return "Met"
  }
  if (result === CRITERION_VALUE.NOT_MET) {
    return "Not met"
  }
  if (result === CRITERION_VALUE.UNKNOWN) {
    return "Unknown"
  }
  return "No review"
}

export const CompositeReviewMatrix: React.FC<CompositeReviewMatrixProps> = ({
  criteria,
  reviewers,
  defaultAllExpanded = false,
  itemLabel = "criterion",
  reviewerColumns = false,
}) => {
  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set(defaultAllExpanded ? criteria.map((_, i) => i) : []),
  )
  const [listVisible, setListVisible] = useState(true)

  const toggle = (i: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(i)) {
        next.delete(i)
      } else {
        next.add(i)
      }
      return next
    })

  const hasReviewers = reviewers.length > 0
  const reviewerGridStyle = hasReviewers
    ? {
        gridTemplateColumns: `repeat(${reviewers.length}, minmax(0, 1fr))`,
      }
    : undefined

  // Build ordered lists of results per criterion name for each reviewer.
  // Using a list rather than a single value preserves duplicate criterion
  // text (allowed in test-case and static-criteria configs) so each row gets
  // its own result rather than every duplicate collapsing to the last one.
  const reviewerNameIndexes = useMemo(
    () =>
      reviewers.map((r) => {
        const map = new Map<string, CriterionResult[]>()
        for (const item of r.results ?? []) {
          if (!item.criterion) {
            continue
          }
          const existing = map.get(item.criterion)
          if (existing) {
            existing.push(item)
          } else {
            map.set(item.criterion, [item])
          }
        }
        return map
      }),
    [reviewers],
  )

  // Precompute which occurrence each canonical criterion is (0 for the first
  // "X", 1 for the second, etc.) so duplicates line up with the reviewer's
  // corresponding entry.
  const criterionOccurrences = useMemo(() => {
    const seen = new Map<string, number>()
    return criteria.map((name) => {
      const count = seen.get(name) ?? 0
      seen.set(name, count + 1)
      return count
    })
  }, [criteria])

  const perCriterion = useMemo(
    () =>
      criteria.map((criterion, i) => ({
        criterion,
        rows: reviewers.map((r, rIndex) => {
          const nameIndex = reviewerNameIndexes[rIndex]
          const byName = nameIndex?.get(criterion)?.[criterionOccurrences[i]]
          // Only fall back to positional if the reviewer has NO named results
          // at all (e.g. a human vote stored as a bare set_value). If the
          // reviewer carries names but doesn't cover this criterion, that's a
          // genuine "no result" — not a reason to borrow another criterion's
          // verdict by index.
          const hasNamedResults = (nameIndex?.size ?? 0) > 0
          const item = byName ?? (hasNamedResults ? undefined : r.results?.[i])
          return {
            key: r.key,
            label: r.label,
            assignee: r.assignee ?? null,
            date: r.date ?? null,
            result: item?.result ?? null,
            explanation: item?.explanation ?? "",
            hasResult: Boolean(item),
            emptyMessage: r.emptyMessage ?? "No review",
          }
        }),
      })),
    [criteria, reviewers, reviewerNameIndexes, criterionOccurrences],
  )

  if (criteria.length === 0) {
    return (
      <p className="text-sm text-coval-text-muted">
        This metric has no criteria configured.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-coval-text-muted uppercase tracking-wide">
          {criteria.length}{" "}
          {criteria.length === 1 ? itemLabel : `${itemLabel}s`}
        </span>
        <button
          type="button"
          onClick={() => setListVisible((v) => !v)}
          className="text-xs font-medium text-coval-text-muted hover:text-coval-text focus-visible:outline-none rounded px-1"
          aria-expanded={listVisible}
        >
          {listVisible ? "Hide all" : "Show all"}
        </button>
      </div>
      {listVisible && (
        <div className="flex flex-col gap-2">
          {reviewerColumns && hasReviewers && (
            <div
              className="grid items-end gap-2 px-3"
              style={reviewerGridStyle}
              aria-hidden
            >
              {reviewers.map((reviewer) => (
                <span
                  key={reviewer.key}
                  className="text-[11px] font-medium text-coval-text-muted"
                >
                  {reviewer.label}
                </span>
              ))}
            </div>
          )}
          <ul className="flex flex-col gap-2">
            {perCriterion.map(({ criterion, rows }, i) => {
              const isOpen = expanded.has(i)
              return (
                <li
                  key={i}
                  className="rounded-md border border-coval-border bg-surface-default"
                >
                  <button
                    type="button"
                    onClick={() => toggle(i)}
                    aria-expanded={isOpen}
                    aria-label={
                      reviewerColumns
                        ? `${criterion}: ${rows
                            .map(
                              (row) =>
                                `${row.label} ${verdictLabel(row.result)}`,
                            )
                            .join(", ")}`
                        : undefined
                    }
                    className={cn(
                      "w-full gap-2 px-3 py-2 text-left focus-visible:outline-none rounded-md",
                      reviewerColumns && hasReviewers
                        ? "flex flex-col"
                        : "flex items-start justify-between",
                    )}
                  >
                    <div className="flex w-full min-w-0 items-start gap-2">
                      {reviewerColumns && (
                        <span
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-2 text-[11px] font-medium text-coval-text-muted"
                          aria-hidden
                        >
                          {i + 1}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-coval-text break-words">
                          {criterion}
                        </p>
                        {hasReviewers && !reviewerColumns && (
                          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                            {rows.map((row) => (
                              <span
                                key={row.key}
                                className="inline-flex items-center gap-1 text-xs text-coval-text-muted"
                              >
                                <VerdictIcon result={row.result} />
                                <span className="text-coval-text">
                                  {row.label}
                                </span>
                                <span className="text-coval-text-muted">
                                  · {verdictLabel(row.result)}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <ChevronDown
                        className={cn(
                          "w-4 h-4 mt-0.5 ml-auto shrink-0 text-coval-text-muted transition-transform",
                          isOpen && "rotate-180",
                        )}
                        aria-hidden
                      />
                    </div>
                    {reviewerColumns && hasReviewers && (
                      <div
                        className="grid w-full gap-2 border-t border-coval-border pt-2"
                        style={reviewerGridStyle}
                      >
                        {rows.map((row) => (
                          <span
                            key={row.key}
                            className="inline-flex min-w-0 items-center gap-1 text-xs text-coval-text"
                          >
                            <VerdictIcon result={row.result} />
                            {verdictLabel(row.result)}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                  {isOpen && hasReviewers && (
                    <div className="border-t border-coval-border px-3 py-2 flex flex-col gap-2">
                      {rows.map((row) => (
                        <div key={row.key} className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1.5 text-xs text-coval-text">
                            <VerdictIcon result={row.result} />
                            <span className="font-medium">{row.label}</span>
                            {row.assignee && (
                              <span className="text-coval-text-muted break-all">
                                · {row.assignee}
                              </span>
                            )}
                            {row.date && (
                              <span className="text-coval-text-muted">
                                · {row.date}
                              </span>
                            )}
                          </div>
                          {row.hasResult ? (
                            row.explanation ? (
                              <p className="text-xs text-coval-text-muted break-words pl-5">
                                {row.explanation}
                              </p>
                            ) : (
                              <p className="text-xs text-coval-text-muted italic pl-5">
                                No explanation
                              </p>
                            )
                          ) : (
                            <p className="text-xs text-coval-text-muted italic pl-5">
                              {row.emptyMessage}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
